import * as React from "react";
import { RadiosEditor } from "../../../../../../../react-core/Html/RadiosEditor";
import { addYears } from "../../../../../../../react-core/utils/date";
import { JsonDateParser } from "../../../../../../../react-core/Utils/JsonDateParser";
import { PensionOption } from "../../../../../../../Wrap/Constants/Enums/Apply/PensionOption";
import { CurrencyCode } from "../../../../../../../Wrap/Constants/Enums/Domain/CurrencyCode";
import { ClientDetails } from "../../../../../../../Wrap/Models/Models/Processes/Quote/ClientDetails";
import { DrawdownModel } from "../../../../../../../Wrap/Models/Models/QuoteAndApplyBase/Shared/DrawdownModel";
import { IJsonDate } from "../../../../../../../Wrap/System/IJsonDate";
import { Alert } from "../../../../../../Shared/Components/Alert";
import { AlertType } from "../../../../../../Shared/Entities/Constants/AlertConstants";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";
import { SectionHeader } from "../../SharedComponents";
import { DrawdownCalculationAction } from "./Action/DrawdownCalculationAction";
import { calculateBeneiftsCharge, calculateBeneiftsNet, shouldRestictUfpls } from "./Helper/DrawdownDetailHelper";
import { PensionOptionMap } from "./Resource/Contents";
import { TextBoxEditor } from "../../../../../../../react-core/Html/TextBoxEditor"
import { DrawdownType } from "../../../../../../../Wrap/Constants/Enums/Drawdown/DrawdownType";
import { CustomerPensionProtectionModel } from "../../../../../../../Wrap/Models/Domains/Drawdown/CustomerPensionProtectionModel";

interface IPensionOptionsProps extends IProcessComponentProps {
    data: DrawdownModel;
    pensionProtection: CustomerPensionProtectionModel;
    clientDetail: ClientDetails;
    validateDrawdown: (data: DrawdownModel) => void;
    subaccountHierarchyId: string;
    instructionOwnerHierarchyId: string;
}

interface IPensionOptionsState {
    dri?: number;
    itfc?: number;
    driAlert: boolean;
    itfcAlert: boolean;
}

export class PensionOptions extends ProcessComponent<IPensionOptionsProps, IPensionOptionsState> {
    constructor(props: any) {
        super(props);
        this.state = {
            driAlert: false,
            itfcAlert: false,
            dri: this.props.data?.DrawdownRequestId,
            itfc: this.props.data?.ITFCRequestId,
        };
    }

    private olderThan(age: number, dateOfBirth: IJsonDate) {
        const now = new Date();
        const birth = JsonDateParser.toDate(dateOfBirth);
        return addYears(birth, age) <= now;
    }

    private hasUFPLS = (): boolean => {
        const { pensionProtection, clientDetail, data } = this.props;
        if (shouldRestictUfpls(pensionProtection, data)) { return false; }
        if (
            this.olderThan(75, clientDetail?.DateOfBirth) &&
            data?.LifetimeAllowance?.AvailablePersonalAllowance?.Amount <= 0
        ) {
            return false;
        }
        if (
            pensionProtection?.PensionSchemeSpecificProtection?.ProtectedRetirementAge &&
            !this.olderThan(pensionProtection?.PensionSchemeSpecificProtection?.ProtectedRetirementAge, clientDetail?.DateOfBirth)
        ) {
            return false;
        }
        return true;
    };

    private hasDrawdown = (): boolean => {
        return true;
    };

    private validateId = (id, isITFC) => {
        if (!id) { return; }
        DrawdownCalculationAction.validateRequestId(id, this.props.data, isITFC, (d: DrawdownModel) => {
            if (d.Status > 0) {
                const instumentsettings = this.props.data?.InvestmentPathwayAnswers?.InvestmentPathwaysSettingsForSettings;
                if(isITFC){
                    this.props.validateDrawdown({
                        ...d,
                        ITFCRequestId: id,
                        InvestmentPathwayAnswers: {
                            ...d?.InvestmentPathwayAnswers,
                            InvestmentPathwaysSettingsForSettings: d?.InvestmentPathwayAnswers?.InvestmentPathwaysSettingsForSettings ?? instumentsettings
                        }
                    });
                }else{
                    this.props.validateDrawdown({
                        ...d,
                        DrawdownRequestId: id,
                        InvestmentPathwayAnswers: {
                            ...d?.InvestmentPathwayAnswers,
                            InvestmentPathwaysSettingsForSettings: d?.InvestmentPathwayAnswers?.InvestmentPathwaysSettingsForSettings ?? instumentsettings
                        }
                    });
                }
            } else {
                if (isITFC) {
                    this.setState({ itfcAlert: true });
                } else {
                    this.setState({ driAlert: true });
                }
            }
        }, this.props.subaccountHierarchyId, this.props.instructionOwnerHierarchyId);
    };

    private convertNumberToString = (num: number, size: number): string => {
        let numStr = num.toString();
        while (numStr.length < size) numStr = "0" + numStr;
        return numStr;
    }

    public render(): JSX.Element {
        const { data, onChange } = this.props;

        const driStr7 = this.state.dri == null ? null : this.convertNumberToString(this.state.dri, 7);
        const itfcStr7 = this.state.itfc == null ? null : this.convertNumberToString(this.state.itfc, 7);
        const meet7LenNumberFormat = (v): boolean => {
            const rExp: RegExp = /[0-9]/g;
            return !v || (v.length == 7 && rExp.test(v));
        }
        return (
            <>
                {SectionHeader("Pension options")}
                <RadiosEditor
                    options={[PensionOption.Drawdown, PensionOption.UFPLS]}
                    getText={(v) => PensionOptionMap[v]}
                    validationRules={[
                        {
                            errorMessage: "Can not select this type",
                            validator: (v) => {
                                if (v == PensionOption.UFPLS) { return this.hasUFPLS(); }
                                if (v == PensionOption.Drawdown) { return this.hasDrawdown(); }
                                return true;
                            },
                        },
                    ]}
                    onChange={(v) =>
                        onChange({
                            PensionOption: v,
                            DrawdownType: DrawdownType.Flexible,
                            GrossExcessBenefitLumpSum: v == PensionOption.UFPLS ? 100 : 0,
                            GrossExcessBenefitTotal: v == PensionOption.UFPLS ? 100 : 0,
                            ExcessBenefitsCalculations: {
                                ...data?.ExcessBenefitsCalculations,
                                LumpSumTaxPayable: {
                                    Amount:
                                        v == PensionOption.UFPLS ? calculateBeneiftsCharge(data, 100, "lumpsum") : 0,
                                    Currency: CurrencyCode.GBP,
                                },
                                LumpSumTotalNetBenefits: {
                                    Amount: v == PensionOption.UFPLS ? calculateBeneiftsNet(data, 100, "lumpsum") : 0,
                                    Currency: CurrencyCode.GBP,
                                },
                            },
                        })
                    }
                    required="Please select Pension options"
                    selected={data?.PensionOption}
                    cssWrapper="rg-inline mb-10"
                />
                <Alert
                    alertType={AlertType.Warning}
                    message={
                        <>
                            <p>
                                If there is insufficient cash to fund the tax-free lump sum, we will sell assets to
                                cover the payment, in line with any deducation preference.
                            </p>
                            <p>
                                Any phasing instructions will cease following a drawdown. Please contact us after
                                submitting the application if you would like phasing to continue.
                            </p>
                            <p>
                                For more information about our drawdown process, please contact our support team
                                at 0345 279 1001.
                            </p>
                        </>
                    }
                />
                {data?.PensionOption != PensionOption.UFPLS && (
                    <>
                        <div className="row">
                            <div className="col-4">
                                <TextBoxEditor
                                    label="Request ID (optional)"
                                    onChange={(v) => {
                                        if (meet7LenNumberFormat(v)) {
                                            this.setState({
                                                driAlert: false,
                                                itfcAlert: false,
                                                dri: !v ? null : +v
                                            });
                                        }
                                    }}
                                    validationRules={[
                                        {
                                            errorMessage: "Invalid format, please re-enter",
                                            validator: meet7LenNumberFormat
                                        },
                                    ]}
                                    maxLength={7}
                                    value={driStr7}
                                    cssWrapper="control-size-245"
                                />
                            </div>
                            <div className="col-4">
                                <button
                                    className="btn secondary mr-10 mt-30"
                                    onClick={() => this.validateId(this.state.dri, false)}
                                >
                                    Validate
                                </button>
                            </div>
                        </div>
                        {this.state.driAlert && (
                            <Alert alertType={AlertType.Error} message={"drawdown request id is not valid"} />
                        )}
                    </>
                )}
                {data?.PensionOption != PensionOption.UFPLS && (
                    <>
                        <div className="row">
                            <div className="col-4">
                                <TextBoxEditor
                                    label="ITFC Request ID (optional)"
                                    validateOnTyping={false}
                                    onChange={(v) => {
                                        if (meet7LenNumberFormat(v)) {
                                            this.setState({ itfcAlert: false, driAlert: false, itfc: !v ? null : +v });
                                        }
                                    }}
                                    validationRules={[
                                        {
                                            errorMessage: "Invalid format, please re-enter",
                                            validator: meet7LenNumberFormat,
                                        },
                                    ]}
                                    maxLength={7}
                                    value={itfcStr7}
                                    cssWrapper="control-size-245"
                                />
                            </div>
                            <div className="col-4">
                                <button
                                    className="btn secondary mr-10 mt-30"
                                    onClick={() => this.validateId(this.state.itfc, true)}
                                >
                                    Validate
                                </button>
                            </div>
                        </div>
                        {this.state.itfcAlert && (
                            <Alert alertType={AlertType.Error} message={"ITFC request id is not valid"} />
                        )}
                    </>
                )}
            </>
        );
    }
}
